import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import {
  EProductCategorySortField,
  IProductCategory,
  PProductCategory,
  PProductCategoryFilter,
} from "../entities";
import IProductCategoryRepository from "../repository";

export default class ProductCategoryRepository
  implements IProductCategoryRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private PRODUCT_CATEGORY_ROUTE = "/admin/product-category";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PProductCategoryFilter) => {
    return filter;
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getProductCategory(
    pagination: IPagination,
    filter: PProductCategoryFilter,
    sorts: ISort<EProductCategorySortField>[],
    signal?: CancelTokenSource
  ): Promise<[IProductCategory[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      (val) => val
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.PRODUCT_CATEGORY_ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getProductCategoryById(id: number): Promise<IProductCategory> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.PRODUCT_CATEGORY_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async createProductCategory(
    productCategory: PProductCategory
  ): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(
        `${this.PRODUCT_CATEGORY_ROUTE}`,
        productCategory
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async updateProductCategory(
    id: number,
    productCategory: PProductCategory
  ): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().patch(
        `${this.PRODUCT_CATEGORY_ROUTE}/${id}`,
        productCategory
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
